import { useEffect, useState } from "react";
import axios from "axios";
import { Download, Lock, LogOut, Search, RefreshCw } from "lucide-react";
import SubHeader from "@/components/SubHeader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "tt_admin_token";

export default function LeadsAdmin() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [passcode, setPasscode] = useState("");
  const [leads, setLeads] = useState(null);
  const [query, setQuery] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchLeads = async (t) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/leads`, { headers: { Authorization: `Bearer ${t}` } });
      setLeads(data);
      setError("");
    } catch {
      sessionStorage.removeItem(TOKEN_KEY);
      setToken("");
      setLeads(null);
      setError("Session expired. Please sign in again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchLeads(token);
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { passcode });
      sessionStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch {
      setError("Invalid passcode.");
      setLoading(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setLeads(null);
    setPasscode("");
  };

  const exportCsv = () => {
    const cols = ["business_name", "country", "business_type", "current_website", "destinations_count", "needs", "name", "email", "phone", "created_at"];
    const esc = (v) => `"${String(Array.isArray(v) ? v.join("; ") : v ?? "").replace(/"/g, '""')}"`;
    const csv = [cols.join(","), ...filtered.map((l) => cols.map((c) => esc(l[c])).join(","))].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "travel-tek-leads.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const q = query.trim().toLowerCase();
  const filtered = (leads || []).filter((l) =>
    !q || [l.business_name, l.country, l.business_type, l.name, l.email, l.phone].some((v) => (v || "").toLowerCase().includes(q))
  );

  return (
    <div className="min-h-screen bg-paper text-ink" data-testid="admin-page">
      <SubHeader cta={false} />
      <main className="container-x py-14">
        {!token ? (
          <div className="mx-auto max-w-md">
            <form onSubmit={login} className="rounded-2xl border border-sand bg-white p-8 shadow-[0_35px_70px_-35px_rgba(22,22,22,0.3)]" data-testid="admin-login-form">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-parch text-ink">
                <Lock className="h-5 w-5" />
              </span>
              <h1 className="mt-5 font-serif text-3xl text-ink">Leads Admin</h1>
              <p className="mt-2 text-sm text-stone2">Enter the admin passcode to view demo requests.</p>
              <input
                type="password"
                value={passcode}
                onChange={(e) => setPasscode(e.target.value)}
                placeholder="Admin passcode"
                data-testid="admin-passcode-input"
                className="mt-6 w-full rounded-xl border border-sand bg-white px-4 py-3.5 text-base text-ink outline-none transition-colors duration-300 placeholder:text-stone2/50 focus:border-terra"
                autoFocus
              />
              {error && <p className="mt-3 text-sm font-medium text-terra" data-testid="admin-login-error">{error}</p>}
              <button
                type="submit"
                disabled={loading || !passcode}
                data-testid="admin-login-btn"
                className="mt-5 w-full rounded-full bg-ink px-6 py-3.5 text-sm font-semibold text-paper transition-colors duration-300 hover:bg-terra disabled:opacity-50"
              >
                {loading ? "Checking…" : "Sign in"}
              </button>
            </form>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-end justify-between gap-5">
              <div>
                <p className="eyebrow">Travel-Tek · Private</p>
                <h1 className="mt-3 font-serif text-4xl text-ink" data-testid="admin-dashboard-title">Demo Requests</h1>
                <p className="mt-2 text-sm text-stone2" data-testid="admin-leads-count">
                  {leads ? `${filtered.length} of ${leads.length} requests` : "Loading…"}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2 rounded-full border border-sand bg-white px-4 py-2.5">
                  <Search className="h-4 w-4 text-stone2" />
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search business, country, contact…"
                    data-testid="admin-search-input"
                    className="w-56 bg-transparent text-sm outline-none placeholder:text-stone2/60"
                  />
                </div>
                <button
                  onClick={() => fetchLeads(token)}
                  data-testid="admin-refresh-btn"
                  className="inline-flex items-center gap-2 rounded-full border border-sand bg-white px-4 py-2.5 text-sm font-semibold text-ink transition-colors duration-300 hover:border-ink"
                >
                  <RefreshCw className="h-4 w-4" /> Refresh
                </button>
                <button
                  onClick={exportCsv}
                  disabled={!filtered.length}
                  data-testid="admin-export-csv-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-paper transition-colors duration-300 hover:bg-terra disabled:opacity-50"
                >
                  <Download className="h-4 w-4" /> Export CSV
                </button>
                <button
                  onClick={logout}
                  data-testid="admin-logout-btn"
                  className="inline-flex items-center gap-2 rounded-full border border-sand px-4 py-2.5 text-sm font-medium text-stone2 transition-colors duration-300 hover:text-ink"
                >
                  <LogOut className="h-4 w-4" /> Logout
                </button>
              </div>
            </div>

            <div className="mt-8 overflow-x-auto rounded-2xl border border-sand bg-white" data-testid="admin-leads-table">
              <table className="w-full min-w-[900px] text-left text-sm">
                <thead>
                  <tr className="border-b border-sand font-mono text-[10px] uppercase tracking-[0.16em] text-stone2">
                    {["Business", "Country", "Type", "Destinations", "Needs", "Contact", "Submitted"].map((h) => (
                      <th key={h} className="px-5 py-4 font-semibold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-sand/70">
                  {filtered.map((l) => (
                    <tr key={l.id} className="transition-colors duration-200 hover:bg-paper/70" data-testid={`admin-lead-row-${l.id}`}>
                      <td className="px-5 py-4 font-semibold text-ink">{l.business_name}</td>
                      <td className="px-5 py-4 text-stone2">{l.country}</td>
                      <td className="px-5 py-4 text-stone2">{l.business_type}</td>
                      <td className="px-5 py-4 text-stone2">{l.destinations_count}</td>
                      <td className="max-w-[200px] px-5 py-4 text-stone2">{(l.needs || []).join(", ")}</td>
                      <td className="px-5 py-4">
                        <p className="font-medium text-ink">{l.name}</p>
                        <p className="text-xs text-stone2">{l.email} · {l.phone}</p>
                      </td>
                      <td className="px-5 py-4 text-xs text-stone2">{new Date(l.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                  {leads && filtered.length === 0 && (
                    <tr><td colSpan={7} className="px-5 py-10 text-center text-stone2" data-testid="admin-leads-empty">No demo requests match.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
